import { type ReactElement, useEffect, useRef, useState } from 'react';
import type * as Adflow from 'adflow';

import { createDomFrameRenderer } from '../animationRender';
import { useTemplateWebMCPFeature } from './hooks/useTemplateWebMCPFeature';
import { openPreviewRasterSession, rasterizePreviewElement } from './capturePreviewImage';
import {
  registerTemplateFrameRenderer,
  registerTemplatePreviewCapture,
} from './previewCaptureRegistry';

type RenderPreview = (args: Adflow.RenderPreviewArgs) => ReactElement;

type Props = {
  args: Adflow.RenderPreviewArgs;
  renderPreview: RenderPreview;
};

type PendingSeek = {
  timeSeconds: number | null;
  resolve: (frame: HTMLElement) => void;
  reject: (error: Error) => void;
};

/** rAF stalls in a background tab; the DOM is already committed, so a short timeout is enough. */
function afterCommit(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(finish));
    setTimeout(finish, 50);
  });
}

/**
 * Off-screen preview used for WebMCP raster capture (no selection overlays).
 * Registered only while cm-template-webmcp is enabled. Animation frames hold the
 * preview at a playhead; the static capture never plays the editor replay.
 */
export function WebMcpTemplatePreviewCapture({ args, renderPreview }: Props): ReactElement | null {
  const featureEnabled = useTemplateWebMCPFeature();
  const containerRef = useRef<HTMLDivElement>(null);
  const [playheadSeconds, setPlayheadSeconds] = useState<number | null>(null);
  const playheadRef = useRef<number | null>(null);
  const pendingSeekRef = useRef<PendingSeek | null>(null);
  const mountedRef = useRef(false);

  const frameElement = (): HTMLElement => {
    const frame = containerRef.current?.querySelector('[data-testid="preview-frame"]');
    if (!(frame instanceof HTMLElement)) {
      throw new Error('The preview frame is not mounted.');
    }
    return frame;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingSeekRef.current?.reject(new Error('The preview was unmounted.'));
      pendingSeekRef.current = null;
    };
  }, []);

  useEffect(() => {
    const pending = pendingSeekRef.current;
    if (!pending || pending.timeSeconds !== playheadSeconds) {
      return;
    }
    pendingSeekRef.current = null;
    afterCommit().then(() => {
      try {
        pending.resolve(frameElement());
      } catch (error) {
        pending.reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }, [playheadSeconds]);

  useEffect(() => {
    if (!featureEnabled) {
      return;
    }

    const seek = (timeSeconds: number | null, signal?: AbortSignal) =>
      new Promise<HTMLElement>((resolve, reject) => {
        if (!mountedRef.current) {
          reject(new Error('The preview was unmounted.'));
          return;
        }
        if (signal?.aborted) {
          reject(new DOMException('The seek was cancelled.', 'AbortError'));
          return;
        }
        pendingSeekRef.current?.reject(new Error('A newer seek replaced this one.'));
        pendingSeekRef.current = null;
        if (playheadRef.current === timeSeconds) {
          afterCommit().then(() => {
            try {
              resolve(frameElement());
            } catch (error) {
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          });
          return;
        }
        pendingSeekRef.current = { timeSeconds, resolve, reject };
        playheadRef.current = timeSeconds;
        setPlayheadSeconds(timeSeconds);
      });

    const unregisterCapture = registerTemplatePreviewCapture(async (options) =>
      rasterizePreviewElement(frameElement(), options),
    );
    const unregisterFrames = registerTemplateFrameRenderer(
      createDomFrameRenderer({ seek }, openPreviewRasterSession),
    );
    return () => {
      unregisterCapture();
      unregisterFrames();
    };
  }, [featureEnabled]);

  if (!featureEnabled) {
    return null;
  }

  const captureArgs: Adflow.RenderPreviewArgs = {
    ...args,
    isIndexPreview: true,
    selectedElementId: null,
    animationPlaybackToken: 0,
    playheadSeconds,
  };

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      data-webmcp-preview-capture-root
    >
      {renderPreview(captureArgs)}
    </div>
  );
}
